import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Copy, Eye, RotateCcw, Sparkles, Wand2 } from "lucide-react";
import { toast } from "sonner";
import { CelebritySearch } from "../components/CelebritySearch";
import { VersusBadge } from "../components/VersusBadge";
import { CompactReasonPicker, CompactFactorPicker } from "../components/Pickers";
import { CELEBRITIES } from "../data/celebrities";
import { REASONS, DAILY_MATCHUPS, DECIDING_FACTORS, DIFFICULTIES } from "../data/gameData";
import { generateCode, saveCustomGame } from "../lib/storage";
import { MASH_FACTORS, DEFAULT_MASH_FACTOR_ID, getMashFactor } from "../data/mashFactors";

const MODES = [
  { id: "quick", title: "Quick Custom", body: "1 custom · 4 auto rounds", customCount: 1 },
  { id: "curated", title: "Curated", body: "3 custom · 2 auto rounds", customCount: 3 },
  { id: "personal", title: "Personal Edition", body: "All 5 hand-picked", customCount: 5 },
];

const randomAutoRound = (id, exclude) => {
  const pool = CELEBRITIES.filter((c) => !exclude.has(c.id));
  const a = pool[Math.floor(Math.random() * pool.length)];
  const rest = pool.filter((c) => c.id !== a.id);
  const b = rest[Math.floor(Math.random() * rest.length)];
  return { id, aId: a.id, bId: b.id, isAuto: true };
};

export default function CustomBuilder() {
  const nav = useNavigate();
  const [step, setStep] = useState("meta"); // meta | rounds | done
  const [mode, setMode] = useState("curated");
  const [organizerName, setOrganizerName] = useState("");
  const [message, setMessage] = useState(
    "I created these matchups because I thought they would reveal your taste better than another boring quiz."
  );
  const [rounds, setRounds] = useState([]); // custom rounds
  const [activeRound, setActiveRound] = useState(0);
  const [pickerSide, setPickerSide] = useState(null); // "a" | "b" | null
  const [code, setCode] = useState(null);
  const [mashFactorId, setMashFactorId] = useState(DEFAULT_MASH_FACTOR_ID);

  const modeObj = MODES.find((m) => m.id === mode);
  const customCount = modeObj.customCount;

  const initRounds = (m) => {
    const cc = MODES.find((x) => x.id === m).customCount;
    setRounds(
      Array.from({ length: cc }).map((_, i) => ({
        id: `cr${i + 1}`,
        aId: null,
        bId: null,
        organizerChoiceId: null,
        organizerPredictionId: null,
        organizerReasons: [],
        organizerDecidingFactor: null,
        organizerPredictedReason: null,
        organizerDifficulty: null,
        organizerNote: "",
      }))
    );
    setActiveRound(0);
  };

  const startRounds = () => {
    if (!organizerName.trim()) {
      toast.error("Add your display name first.");
      return;
    }
    initRounds(mode);
    setStep("rounds");
  };

  const updateActive = (patch) => {
    setRounds((prev) => prev.map((r, i) => (i === activeRound ? { ...r, ...patch } : r)));
  };

  const round = rounds[activeRound];

  const canFinishRound = round && round.aId && round.bId && round.organizerChoiceId && round.organizerPredictionId;

  const finalizeAndPublish = () => {
    // Fill remaining with auto rounds
    const exclude = new Set(rounds.flatMap((r) => [r.aId, r.bId]).filter(Boolean));
    const autoCount = 5 - rounds.length;
    const autoRounds = [];
    for (let i = 0; i < autoCount; i++) {
      const r = randomAutoRound(`auto${i + 1}`, exclude);
      exclude.add(r.aId);
      exclude.add(r.bId);
      // Seed organizer predictions for auto rounds from the majority-style seed (random)
      const predSide = Math.random() > 0.5 ? r.aId : r.bId;
      autoRounds.push({
        ...r,
        organizerChoiceId: predSide,
        organizerPredictionId: predSide,
        organizerReasons: [],
        organizerDecidingFactor: null,
        organizerPredictedReason: null,
        organizerDifficulty: null,
        organizerNote: "",
      });
    }
    const gameCode = generateCode();
    const game = {
      code: gameCode,
      mode,
      organizerName: organizerName.trim(),
      invitationMessage: message.trim(),
      mashFactorId,
      mashFactorLabel: getMashFactor(mashFactorId).label,
      rounds: [...rounds, ...autoRounds],
      status: "published",
      createdAt: new Date().toISOString(),
    };
    saveCustomGame(game);
    setCode(gameCode);
    setStep("done");
  };

  const handleContinueRound = () => {
    if (!canFinishRound) {
      toast.error("Pick both celebrities, your choice, and your prediction.");
      return;
    }
    if (activeRound < rounds.length - 1) setActiveRound(activeRound + 1);
    else finalizeAndPublish();
  };

  const inviteUrl = code
    ? `${typeof window !== "undefined" ? window.location.origin : "https://celebritysmash.app"}/play/${code}`
    : "";

  const copyLink = async () => {
    await navigator.clipboard.writeText(inviteUrl);
    toast.success("Invitation link copied.");
  };

  // ---------- Render ----------

  if (step === "done") {
    return (
      <div className="min-h-screen spotlight grain scanlines">
        <div className="max-w-md mx-auto px-5 pt-6 pb-nav">
          <button data-testid="builder-home-btn" onClick={() => nav("/home")} className="flex items-center gap-1 text-sm text-[#FDFBF7]/70 hover:text-[#FF107A]">
            <ArrowLeft size={16} /> Home
          </button>
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="mt-4">
            <div className="text-[11px] tracking-[0.35em] uppercase text-[#00E5FF] font-semibold">Published</div>
            <h1 className="font-display text-5xl tracking-tight mt-2">Invitation Ready</h1>
            <p className="mt-2 text-sm text-[#FDFBF7]/70">
              {mode === "personal" ? "Made especially for the invited player." : "Share your custom smash with anyone."}
            </p>
            <div className="mt-6 rounded-2xl border border-[#FF107A]/40 bg-[#FF107A]/5 p-4">
              <div className="text-[10px] tracking-[0.3em] uppercase text-[#FF107A] font-bold">Invitation link</div>
              <div data-testid="invite-link" className="font-mono text-lg mt-2 text-[#FDFBF7] break-all">
                {inviteUrl}
              </div>
            </div>
            <div className="mt-4 grid grid-cols-2 gap-3">
              <button data-testid="copy-invite-btn" onClick={copyLink} className="rounded-full bg-[#FF107A] py-3 font-bold glow-pink flex items-center justify-center gap-2">
                <Copy size={16} /> Copy Link
              </button>
              <button
                data-testid="preview-game-btn"
                onClick={() => nav(`/play/${code}`)}
                className="rounded-full border-2 border-[#00E5FF] text-[#00E5FF] py-3 font-bold flex items-center justify-center gap-2"
              >
                <Eye size={16} /> Preview
              </button>
            </div>
            <button
              data-testid="start-over-btn"
              onClick={() => {
                setStep("meta");
                setRounds([]);
                setCode(null);
              }}
              className="mt-3 w-full rounded-full border-2 border-white/15 py-3 flex items-center justify-center gap-2"
            >
              <RotateCcw size={16} /> Start Over
            </button>
          </motion.div>
        </div>
      </div>
    );
  }

  if (step === "meta") {
    return (
      <div className="min-h-screen spotlight grain scanlines">
        <div className="max-w-md mx-auto px-5 pt-6 pb-nav">
          <button data-testid="builder-back-btn" onClick={() => nav("/home")} className="flex items-center gap-1 text-sm text-[#FDFBF7]/70 hover:text-[#FF107A]">
            <ArrowLeft size={16} /> Back
          </button>
          <h1 className="font-display text-5xl tracking-tight mt-4">CREATE A SMASH</h1>
          <p className="text-[#FDFBF7]/70 mt-2 text-sm">Build 5 rounds. Predict how your friend answers. Reveal the connection.</p>

          <div className="mt-6">
            <label className="text-[10px] tracking-[0.3em] uppercase text-[#00E5FF] font-bold">Your display name</label>
            <input
              data-testid="organizer-name-input"
              value={organizerName}
              onChange={(e) => setOrganizerName(e.target.value)}
              placeholder="Daniel"
              className="mt-2 w-full rounded-xl bg-black/30 border border-white/15 px-4 py-3 text-[#FDFBF7] focus:outline-none focus:border-[#FF107A]"
            />
          </div>

          <div className="mt-4">
            <label className="text-[10px] tracking-[0.3em] uppercase text-[#00E5FF] font-bold">Invitation message (optional)</label>
            <textarea
              data-testid="invite-message-input"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={3}
              className="mt-2 w-full rounded-xl bg-black/30 border border-white/15 px-4 py-3 text-[#FDFBF7] focus:outline-none focus:border-[#FF107A] resize-none"
            />
          </div>

          <div className="mt-6">
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#A855F7] font-bold">Game mode</div>
            <div className="mt-2 space-y-2">
              {MODES.map((m) => (
                <button
                  key={m.id}
                  data-testid={`mode-${m.id}`}
                  onClick={() => setMode(m.id)}
                  className={`w-full text-left rounded-2xl border-2 p-4 transition-all ${
                    mode === m.id ? "border-[#FF107A] bg-[#FF107A]/10" : "border-white/10 hover:border-[#A855F7]"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-display text-2xl tracking-tight">{m.title}</div>
                      <div className="text-xs text-[#FDFBF7]/60 mt-0.5">{m.body}</div>
                    </div>
                    {m.id === "personal" && <Sparkles size={16} className="text-[#FFD700]" />}
                  </div>
                </button>
              ))}
            </div>
          </div>

          <div className="mt-6">
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#FFD700] font-bold">Smash Factor</div>
            <div className="text-[11px] text-[#FDFBF7]/60 mt-1">The question your friend answers on every match.</div>
            <div className="mt-2 grid grid-cols-2 gap-2" data-testid="mf-picker">
              {MASH_FACTORS.slice(0, 10).map((f) => (
                <button
                  key={f.id}
                  data-testid={`mf-${f.id}`}
                  onClick={() => setMashFactorId(f.id)}
                  className={`rounded-xl border-2 px-3 py-2 text-left text-xs font-semibold leading-tight transition-all ${
                    mashFactorId === f.id
                      ? "border-[#FFD700] bg-[#FFD700]/10 text-[#FDFBF7]"
                      : "border-white/10 bg-black/30 text-[#FDFBF7]/70 hover:border-[#FFD700]/60"
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>
          </div>

          <button
            data-testid="build-rounds-btn"
            onClick={startRounds}
            className="mt-8 w-full rounded-full bg-[#FF107A] py-4 font-bold text-[#FDFBF7] glow-pink flex items-center justify-center gap-2"
          >
            <Wand2 size={16} /> Build my rounds
          </button>
        </div>
      </div>
    );
  }

  // step === "rounds"
  return (
    <div className="min-h-screen spotlight grain scanlines">
      <div className="max-w-md mx-auto px-5 pt-6 pb-nav">
        <button data-testid="rounds-back-btn" onClick={() => setStep("meta")} className="flex items-center gap-1 text-sm text-[#FDFBF7]/70 hover:text-[#FF107A]">
          <ArrowLeft size={16} /> Back
        </button>
        <div className="mt-4 flex items-center justify-between">
          <div>
            <div className="text-[11px] tracking-[0.3em] uppercase text-[#00E5FF] font-bold">
              Custom Round {activeRound + 1} of {rounds.length}
            </div>
            <h2 className="font-display text-3xl tracking-tight">Design the matchup</h2>
          </div>
          <div className="text-[10px] tracking-[0.2em] uppercase text-[#FDFBF7]/50">{modeObj.title}</div>
        </div>

        {/* Two slots */}
        <div className="relative mt-6 grid grid-cols-2 gap-3">
          <Slot
            side="a"
            celebId={round.aId}
            onOpen={() => setPickerSide("a")}
            testId={`slot-a-${activeRound}`}
          />
          <Slot
            side="b"
            celebId={round.bId}
            onOpen={() => setPickerSide("b")}
            testId={`slot-b-${activeRound}`}
          />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-10">
            <VersusBadge small />
          </div>
        </div>

        {pickerSide && (
          <div className="mt-4">
            <CelebritySearch
              testId={`picker-${pickerSide}`}
              excludeIds={[round.aId, round.bId].filter(Boolean)}
              onPick={(c) => {
                updateActive({ [`${pickerSide}Id`]: c.id });
                setPickerSide(null);
              }}
            />
          </div>
        )}

        {round.aId && round.bId && (
          <div className="mt-6 space-y-5">
            <ChoiceGroup
              label="Who would you personally pick?"
              options={[round.aId, round.bId]}
              value={round.organizerChoiceId}
              onChange={(id) => updateActive({ organizerChoiceId: id })}
              testPrefix={`own-${activeRound}`}
            />
            <ChoiceGroup
              label="Who do you think they will pick?"
              options={[round.aId, round.bId]}
              value={round.organizerPredictionId}
              onChange={(id) => updateActive({ organizerPredictionId: id })}
              testPrefix={`pred-${activeRound}`}
            />
            <ReasonsQuick
              value={round.organizerReasons}
              onChange={(v) => updateActive({ organizerReasons: v })}
            />
            <PredictedReason
              value={round.organizerPredictedReason}
              onChange={(f) => updateActive({ organizerPredictedReason: f })}
            />
            <DifficultyQuick
              value={round.organizerDifficulty}
              onChange={(d) => updateActive({ organizerDifficulty: d })}
            />
            <div>
              <label className="text-[10px] tracking-[0.3em] uppercase text-[#00E5FF] font-bold">Personal note (optional)</label>
              <textarea
                data-testid={`note-${activeRound}`}
                value={round.organizerNote}
                onChange={(e) => updateActive({ organizerNote: e.target.value })}
                rows={2}
                placeholder="A private note revealed only after they finish..."
                className="mt-2 w-full rounded-xl bg-black/30 border border-white/15 px-4 py-3 text-sm focus:outline-none focus:border-[#FF107A] resize-none"
              />
            </div>
          </div>
        )}

        <button
          data-testid="round-continue-btn"
          onClick={handleContinueRound}
          disabled={!canFinishRound}
          className={`mt-8 w-full rounded-full py-4 font-bold flex items-center justify-center gap-2 transition-all ${
            canFinishRound ? "bg-[#FF107A] text-[#FDFBF7] glow-pink" : "bg-white/10 text-[#FDFBF7]/40 cursor-not-allowed"
          }`}
        >
          {activeRound < rounds.length - 1 ? "Next Round" : "Publish Invitation"}
        </button>
      </div>
    </div>
  );
}

const Slot = ({ celebId, onOpen, testId }) => {
  const c = CELEBRITIES.find((x) => x.id === celebId);
  if (!c) {
    return (
      <button
        data-testid={testId}
        onClick={onOpen}
        className="aspect-[3/4] rounded-2xl border-2 border-dashed border-white/20 bg-white/5 flex flex-col items-center justify-center gap-2 hover:border-[#00E5FF] hover:bg-[#00E5FF]/5 transition-all"
      >
        <Wand2 size={22} className="text-[#00E5FF]" />
        <span className="font-display text-xl tracking-widest">PICK</span>
        <span className="text-[10px] tracking-[0.2em] uppercase text-[#FDFBF7]/50">Tap to search</span>
      </button>
    );
  }
  return (
    <button
      data-testid={testId}
      onClick={onOpen}
      className="relative overflow-hidden aspect-[3/4] rounded-2xl border-2 border-white/15"
      style={{ background: `linear-gradient(135deg, ${c.palette[0]}, ${c.palette[1]})` }}
    >
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="font-display text-6xl opacity-40" style={{ color: c.palette[2] }}>
          {c.name.split(" ").map((p) => p[0]).join("").slice(0, 2)}
        </span>
      </div>
      <div className="absolute inset-x-0 bottom-0 p-3 bg-gradient-to-t from-black/90 to-transparent text-left">
        <div className="font-display text-xl leading-none">{c.name}</div>
        <div className="text-[10px] tracking-[0.2em] uppercase text-[#FDFBF7]/60 mt-1">{c.category}</div>
      </div>
      <div className="absolute top-2 right-2 text-[10px] tracking-[0.2em] uppercase bg-black/60 px-2 py-0.5 rounded-full">
        Change
      </div>
    </button>
  );
};

const ChoiceGroup = ({ label, options, value, onChange, testPrefix }) => (
  <div>
    <p className="text-xs tracking-[0.2em] uppercase text-[#00E5FF] font-semibold mb-2">{label}</p>
    <div className="grid grid-cols-2 gap-2">
      {options.map((id) => {
        const c = CELEBRITIES.find((x) => x.id === id);
        const sel = value === id;
        return (
          <button
            key={id}
            data-testid={`${testPrefix}-${id}`}
            onClick={() => onChange(id)}
            className={`rounded-xl border-2 py-3 px-3 text-sm text-left transition-all ${
              sel ? "border-[#FF107A] bg-[#FF107A]/10" : "border-white/10 hover:border-white/30"
            }`}
          >
            <div className="font-semibold truncate">{c?.name}</div>
          </button>
        );
      })}
    </div>
  </div>
);

const ReasonsQuick = ({ value, onChange }) => {
  const toggle = (id) => {
    if (value.includes(id)) onChange(value.filter((x) => x !== id));
    else if (value.length < 2) onChange([...value, id]);
  };
  return (
    <div>
      <p className="text-xs tracking-[0.2em] uppercase text-[#00E5FF] font-semibold mb-2">Your reasons (up to 2)</p>
      <div className="flex flex-wrap gap-1.5" data-testid="own-reasons">
        {REASONS.map((r) => (
          <button
            key={r.id}
            data-testid={`own-reason-${r.id}`}
            onClick={() => toggle(r.id)}
            className={`text-xs px-3 py-1.5 rounded-full border transition-all ${
              value.includes(r.id) ? "bg-[#FF107A] border-[#FF107A]" : "bg-white/5 border-white/15 hover:border-[#00E5FF]"
            }`}
          >
            {r.label}
          </button>
        ))}
      </div>
    </div>
  );
};

const PredictedReason = ({ value, onChange }) => (
  <div>
    <p className="text-xs tracking-[0.2em] uppercase text-[#00E5FF] font-semibold mb-2">Predict their deciding factor</p>
    <div className="flex flex-wrap gap-1.5" data-testid="predicted-reason">
      {DECIDING_FACTORS.map((f) => (
        <button
          key={f}
          data-testid={`predicted-reason-${f.toLowerCase().replace(/\s+/g, "-")}`}
          onClick={() => onChange(value === f ? null : f)}
          className={`text-xs px-3 py-1.5 rounded-full border transition-all ${
            value === f ? "bg-[#00E5FF] text-[#0A090D] border-[#00E5FF]" : "bg-white/5 border-white/15 hover:border-[#00E5FF]"
          }`}
        >
          {f}
        </button>
      ))}
    </div>
  </div>
);

const DifficultyQuick = ({ value, onChange }) => (
  <div>
    <p className="text-xs tracking-[0.2em] uppercase text-[#00E5FF] font-semibold mb-2">How hard did you find it?</p>
    <div className="grid grid-cols-4 gap-1.5" data-testid="own-difficulty">
      {DIFFICULTIES.map((d) => (
        <button
          key={d.id}
          data-testid={`own-difficulty-${d.id}`}
          onClick={() => onChange(value === d.id ? null : d.id)}
          className={`text-xs py-2 rounded-lg border transition-all ${
            value === d.id ? "bg-[#A855F7] border-[#A855F7]" : "bg-white/5 border-white/15 hover:border-[#A855F7]"
          }`}
        >
          {d.label}
        </button>
      ))}
    </div>
  </div>
);
